import { useConfig } from '@/hooks/useConfig';
import { cn } from '@/lib/cn';
import type { Preview } from '@/providers/windowkeeperProvider';
import { Icons } from '@/components/icons';
import type {
  BaseInstance,
  IBaseInstanceConfig,
  ModelName,
} from '@pigello/pigello-matrix';
import type { ReactNode, Ref } from 'react';
import { getTabIcon } from './tab-icon';

type WindowKeeperItemProps = {
  itemWidth: number;
  preview: Preview;
  onPreviewClick: (
    preview: Preview,
    config?: IBaseInstanceConfig<BaseInstance>
  ) => void;
  removePreview: (previewIdentifier: string) => void;
  title: string | ReactNode;
  ref?: Ref<HTMLButtonElement>;
};

function WindowKeeperItem({
  itemWidth,
  preview,
  onPreviewClick,
  removePreview,
  title,
  ref,
  ...props
}: WindowKeeperItemProps) {
  const { config } = useConfig(preview.display.split(':')[0] as ModelName);

  return (
    <button
      {...props}
      ref={ref}
      style={{
        maxWidth: itemWidth,
        minWidth: itemWidth,
        width: itemWidth,
      }}
      className={cn(
        'group relative flex shrink-0 cursor-pointer items-center gap-2 rounded-md border border-border bg-surface px-2 py-1 whitespace-nowrap transition-all hover:scale-[99%] hover:bg-accent',
        {
          'justify-center': itemWidth < 81,
          'bg-yellow-100': preview.type === 'nonsavedinstance',
        }
      )}
      onClick={() => onPreviewClick(preview, config)}
    >
      {getTabIcon(preview.display.split(':')[0].toLowerCase() as ModelName) ?? (
        <Icons.objects className='size-4 min-h-[16px] min-w-[16px]' />
      )}
      {itemWidth > 80 && (
        <div className='flex flex-col justify-start truncate'>
          <span className='-mb-0.5 truncate text-start text-[10px] leading-tight'>
            {config?.verboseName}
          </span>
          <span className='truncate text-start text-xs leading-tight'>
            {title}
          </span>
        </div>
      )}
      <div
        role='button'
        aria-label='Ta bort flik'
        tabIndex={0}
        onKeyDown={(e) => {
          e.preventDefault();
          if (e.key === 'Enter' || e.key === ' ') {
            removePreview(preview.identifier);
          }
        }}
        className='invisible absolute -top-2 -right-2 flex size-5 items-center justify-center rounded-full border border-border bg-surface transition-all group-hover:visible hover:bg-destructive hover:text-destructive-subtle'
        onClick={() => removePreview(preview.identifier)}
      >
        <Icons.close className='size-3' />
      </div>
    </button>
  );
}

export default WindowKeeperItem;
